import { Link } from "react-router-dom";
import { Trees, Droplets, Users, ArrowRight } from "lucide-react";

const principles = [
  {
    icon: Trees,
    num: "01",
    title: "Shade-first design",
    body:
      "In hot remote communities, shade is infrastructure. Multi-layer canopy, windbreaks, nurse trees and protected microclimates — reducing heat load, evapotranspiration and dust before a single crop is planted.",
  },
  {
    icon: Droplets,
    num: "02",
    title: "Water harvesting over irrigation",
    body:
      "Build a sponge, not a hydroponic dependency. Contour planting, infiltration basins, deep mulch and biochar — passive systems that hold moisture in the soil rather than relying on pumps, drip lines and imported parts.",
  },
  {
    icon: Users,
    num: "03",
    title: "Orchards as social infrastructure",
    body:
      "Shade creates gathering. Gathering creates participation. Participation creates ownership. A productive shaded landscape rebuilds the relationship between Elders, young people, women's groups, rangers and place itself.",
  },
];

export default function OrchardsTeaser() {
  return (
    <section
      id="orchards"
      data-testid="orchards-teaser-section"
      className="relative bg-charcoal text-sand py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-ochre mb-6">
              Orchards as Living Infrastructure
            </div>
            <h2
              data-testid="orchards-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-sand"
            >
              Not rows of fruit trees.
              <span className="block italic text-ochre">
                Living ecological infrastructure.
              </span>
            </h2>
          </div>

          <div className="md:col-span-5 space-y-5 text-base md:text-lg leading-relaxed text-sand/80">
            <p>
              Orchards are not a side idea for remote food security. They are
              one of the few systems that can build <em>multi-decade</em>{" "}
              resilience — if they are designed properly.
            </p>
            <p>
              In remote Australia, an orchard should be understood as a layered
              food and water system woven into community infrastructure,
              shade, soil repair, nutrition, cultural practice and local
              enterprise.
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-ochre leading-snug pt-2">
              Food forests and regenerative orchard systems are designed to
              function as living ecosystems — where soil biology, beneficial
              insects, water, plants and people work together to create
              long-term food security.
            </p>
          </div>
        </div>

        {/* 3 strongest principles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-sand/10 border border-sand/15">
          {principles.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.num}
                data-testid={`orchard-principle-${i}`}
                className="bg-charcoal p-8 md:p-10 group hover:bg-sand hover:text-charcoal transition-colors duration-500"
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon
                    className="w-7 h-7 text-ochre group-hover:text-rust transition-colors"
                    strokeWidth={1.5}
                  />
                  <span className="font-serif text-3xl text-sand/40 group-hover:text-rust/40 tick-num leading-none transition-colors">
                    {p.num}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">
                  {p.title}
                </h3>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-sand/75 group-hover:text-ink transition-colors">
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA strip */}
        <div className="mt-16 md:mt-20 pt-10 border-t border-sand/15 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="font-serif text-2xl md:text-3xl italic font-light leading-snug max-w-3xl">
            Four more principles, the systems thinking behind them, and how an
            orchard becomes <span className="not-italic text-ochre">community resilience infrastructure</span> — read the full argument.
          </p>
          <Link
            to="/orchards"
            data-testid="orchards-read-more"
            className="inline-flex items-center gap-3 bg-ochre text-charcoal px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-sand transition-colors self-start md:self-auto"
          >
            Read the full thinking
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
